/**
 * The overview's arithmetic. No React, no fetch, no clock.
 *
 * Everything here exists to stop one class of mistake: a number on a dashboard
 * that is technically computed and factually meaningless. Three rules do most
 * of the work.
 *
 *  - A rate with no denominator is NOT zero. Nought replies out of nought
 *    letters is "nothing to measure", and printing "0%" for it claims a result
 *    where there is no result yet.
 *  - `sent` is not "how many letters went out". A ledger row holds one status
 *    and later facts overwrite earlier ones, so the count of `sent` rows falls
 *    as replies and bounces arrive. Anything that means the size of a send sums
 *    the contacted statuses instead - the list lives in `@wa/core` so this and
 *    the server's delete guard cannot disagree.
 *  - Bounced letters were never delivered, so they do not belong in the
 *    denominator of a reply rate.
 */
import { MKT_CONTACTED_STATUSES, mktContactedTotal } from "@wa/core/marketing-ledger";
import type { RunStop } from "@wa/core/marketing-run";
import type {
  DailyPoint,
  MarketingOverview,
  Readiness,
  SectorRow,
  SectorView,
  StatusTotals,
} from "../../../../lib/marketingOverview";

/** A status count, with the omitted zeroes read as zero. */
export function count(totals: StatusTotals, status: string): number {
  return totals[status] ?? 0;
}

/** Every row in the ledger, whatever became of it. */
export function enrolledTotal(totals: StatusTotals): number {
  return Object.values(totals).reduce((n, v) => n + v, 0);
}

/**
 * How many addresses have actually been written to.
 *
 * The number an operator means by "sent". See the module note above for why it
 * is not `totals.sent`.
 */
export function contactedTotal(totals: StatusTotals): number {
  return mktContactedTotal(totals);
}

/**
 * Letters that reached a mailbox.
 *
 * Contacted minus bounced. A bounce is a letter that was refused at the far
 * end, and counting it as an audience for a reply rate understates every
 * campaign by exactly its worst addresses.
 */
export function deliveredTotal(totals: StatusTotals): number {
  return contactedTotal(totals) - count(totals, "bounced");
}

/**
 * A share, or null when there is nothing to divide by.
 *
 * Null is the whole point. The caller renders it as a dash and says what is
 * missing, rather than as a comfortable zero.
 */
export function rateOf(part: number, whole: number): number | null {
  if (!Number.isFinite(part) || !Number.isFinite(whole) || whole <= 0) return null;
  return part / whole;
}

/**
 * The cached counters have drifted from the ledger, or null when they agree.
 *
 * The campaign list renders from `MktCampaign`'s eight denormalised columns,
 * which are recounted from the ledger on every event that touches it. When the
 * two disagree, one of those recounts did not happen - and the visible symptom
 * is two screens showing different sizes for the same send, with nothing on
 * either of them admitting it. Measuring it here is cheap; noticing it by
 * accident is not.
 */
export interface Drift {
  ledger: number;
  cached: number;
}

export function contactedDrift(ledger: StatusTotals, cached: StatusTotals): Drift | null {
  const a = contactedTotal(ledger);
  const b = contactedTotal(cached);
  return a === b ? null : { ledger: a, cached: b };
}

/**
 * Why nothing is going out, or null when nothing is in the way.
 *
 * ⚠ THE ORDER IS THE SENDER'S ORDER. `preflight()` in the API's
 * `lib/send/run.ts` asks: hard stop, public origin, test mode, channel,
 * campaign status, circuit breaker. This walks the same list so the page names
 * the gate the sender would actually refuse at. Answering in any other order
 * produces advice that is true and useless - "switch the automation on" while a
 * hard stop is pulled sends somebody to the wrong screen.
 *
 * The last two cases are this page's own, because they are not refusals: a
 * sender with nothing active and nothing queued is not blocked, it simply has
 * no work. An operator staring at zeroes needs to be told that too.
 */
export type SendBlocker =
  /** A refusal the sender itself has wording for (`marketing.run.stop.<code>`). */
  | { kind: "stop"; code: RunStop }
  /** Nothing is wrong; there is just no work (`marketing.overview.blocked.<code>`). */
  | { kind: "idle"; code: "noCampaign" | "nothingQueued" | "autopilotOff" };

export function sendBlocker(view: {
  readiness: Readiness;
  campaigns: StatusTotals;
  ledger: StatusTotals;
  health: Pick<MarketingOverview["health"], "trip">;
}): SendBlocker | null {
  const r = view.readiness;
  if (r.stopped) return { kind: "stop", code: "global_stop" };
  if (!r.mailableBase) return { kind: "stop", code: "unmailable_base" };
  if (r.testMode) return { kind: "stop", code: "test_mode" };
  if (!r.channelConfigured) return { kind: "stop", code: "channel_unavailable" };
  if (count(view.campaigns, "active") === 0) return { kind: "idle", code: "noCampaign" };
  if (view.health.trip) {
    return {
      kind: "stop",
      code: view.health.trip.reason === "bounce" ? "breaker_bounce" : "breaker_complaint",
    };
  }
  // Global on purpose: with no pending row anywhere, no campaign has anything
  // to pick up. A per-campaign version of this question belongs on the campaign.
  if (count(view.ledger, "pending") === 0) return { kind: "idle", code: "nothingQueued" };
  // Last, and only when everything else is clear. The automation being off is
  // not a blocker - an operator can start a batch by hand - so it is the reason
  // of last resort rather than the first thing shouted.
  if (!r.autopilotEnabled) return { kind: "idle", code: "autopilotOff" };
  return null;
}

/**
 * The ledger's rows in the order a letter passes through them.
 *
 * A funnel, not an alphabet: enrolled, screened out, waiting, gone, and then
 * the four things that can come back. Read top to bottom it is the life of one
 * address, which is what makes a column of counts legible without a legend.
 */
export const LEDGER_ORDER = [
  "pending",
  "sending",
  "skipped",
  "failed",
  "sent",
  "replied",
  "bounced",
  "unsubscribed",
  "complained",
] as const;

export type LedgerStatus = (typeof LEDGER_ORDER)[number];

/** Statuses that mean a letter left the building, for the interface to mark. */
export const CONTACTED: ReadonlySet<string> = new Set(MKT_CONTACTED_STATUSES);

/** The tallest day in the window, or 0. What the chart scales against. */
export function peakDay(daily: DailyPoint[]): number {
  return daily.reduce((max, d) => (d.sent > max ? d.sent : max), 0);
}

/** Everything the window sent. Zero means the chart has nothing to draw. */
export function windowTotal(daily: DailyPoint[]): number {
  return daily.reduce((n, d) => n + d.sent, 0);
}

/** The sector comparison, ready to draw. */
export interface SectorChart {
  /** Sections the survey covers, biggest slice of our mirror first. */
  rows: SectorRow[];
  /** Active companies sitting in sections the survey does not cover at all
   *  (`O` public administration, `T` households as employers, `U`
   *  extraterritorial bodies). Reported so the rows below add up to something
   *  a reader can name. */
  outsideSurvey: number;
}

/**
 * Which sections to draw, and in what order.
 *
 * DROPS ONLY WHAT CANNOT BE COMPARED. A section with no survey figure has
 * nothing to sit beside, and drawing it with one empty bar invites the reading
 * "nobody in public administration has a website". It is counted into
 * `outsideSurvey` instead, so it leaves the chart without leaving the total.
 *
 * BIGGEST FIRST, by how many companies OUR MIRROR holds - not by the size of
 * the gap. The gap is largest in agriculture, which is also where the register
 * is least useful to us; sorting by our own population puts the sections an
 * operator will actually build an audience from at the top.
 */
export function sectorChart(view: SectorView): SectorChart {
  const rows = view.rows
    .filter((r) => r.statPct != null && r.total > 0)
    .sort((a, b) => b.total - a.total);
  const outsideSurvey = view.rows
    .filter((r) => r.statPct == null)
    .reduce((n, r) => n + r.total, 0);
  return { rows, outsideSurvey };
}

/**
 * How much of the mirror this chart speaks for.
 *
 * Everything the chart drops, named: the sections with no survey figure and the
 * companies with no activity code at all. A share printed without this is a
 * share of a denominator the reader was never told about.
 */
export function sectorCoveredTotal(view: SectorView): number {
  return sectorChart(view).rows.reduce((n, r) => n + r.total, 0);
}
